/**
 * A scheduled task's chips on Discover (#2303): its status (`$status`) and how
 * its last run ended (`$lastRun`, read by the server as the task's History tab
 * reads a run, #2207), in words and a tone.
 */
import type { Task } from '@/types';

export type ChipTone = 'ok' | 'info' | 'warn' | 'err' | 'muted';

export interface Chip {
  label: string;
  tone: ChipTone;
}

function titleCase(value: string): string {
  const v = value.replace(/_/g, ' ').toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const STATUS: Record<string, Chip> = {
  WAITING: { label: 'Waiting', tone: 'muted' },
  RUNNING: { label: 'Running', tone: 'info' },
  DISABLED: { label: 'Disabled', tone: 'muted' },
  SUSPENDED: { label: 'Suspended', tone: 'warn' },
};

/** A task's status chip, from the server's `$status`. */
export function statusChip(task: Pick<Task, '$status' | 'enabled'>): Chip {
  const status = task.$status?.toUpperCase();
  if (status) return STATUS[status] ?? { label: titleCase(status), tone: 'muted' };
  return task.enabled === false ? STATUS.DISABLED : { label: 'Unknown', tone: 'muted' };
}

const OUTCOMES: Record<string, Chip> = {
  COMPLETED: { label: 'Completed', tone: 'ok' },
  SKIPPED: { label: 'Skipped', tone: 'warn' },
  FAILED: { label: 'Failed', tone: 'err' },
};

/** How a task's last run ended, as a chip: "Never run" when it has none. */
export function outcomeChip(task: Pick<Task, '$lastRun'>): Chip {
  const run = task.$lastRun;
  if (!run) return { label: 'Never run', tone: 'muted' };
  const outcome = run.outcome?.toUpperCase();
  if (!outcome) return { label: 'Ran', tone: 'muted' };
  return OUTCOMES[outcome] ?? { label: titleCase(outcome), tone: 'muted' };
}
